import React, { useEffect } from "react";

import { ManualVerificationPending } from "@/components/ManualVerificationPending";

import { StepAccordion } from "./components/StepAccordion";
import { StepFlowPanel } from "./components/StepFlowPanel";
import { WizardNav } from "./components/WizardNav";
import {
    isStepHidden,
    REWARDS_DESTINATION_VARIANT,
    type RewardsDestinationVariant,
    WIZARD_STEPS
} from "./steps";
import { type StepFlow, useStepFlow } from "./useStepFlow";

/**
 * The add-position walkthrough body — the left numbered step rail paired with
 * the right progressive vertical stack (completed steps collapse to summary
 * rows; the current step is expanded + interactive). Driven by `useStepFlow`
 * (position discovery, EOA/Squads verify, create-position submit).
 *
 * This is the single "new style" walkthrough shared by both add-position entry
 * points: the Positions empty state, and the Positions page's `/positions/add`
 * mode (reached from "+New Address" or a Dashboard link). Callers supply only
 * the desktop gate + a completion callback; all wizard state lives in the hook.
 */
interface PositionWalkthroughProps {
    /** When true, the primary CTA is disabled and a desktop hint is shown. */
    viewOnly: boolean;
    /** Called once the in-content "Submit Position" flow completes — lets the
     *  host reveal the positions table / close the inline flow. */
    onSubmitted: () => void;
    /** Lets the host hide flow chrome while the saved review receipt is shown. */
    onPendingReviewChange?: (pending: boolean) => void;
}

export function PositionWalkthrough({
    viewOnly,
    onSubmitted,
    onPendingReviewChange
}: PositionWalkthroughProps): React.ReactElement {
    // All wizard state + gating + reveal timing lives in the hook; the layout
    // below only renders what it returns.
    const flow = useStepFlow();
    const pendingReview =
        flow.submit.submitted && Boolean(flow.submit.pendingReview);
    useEffect(() => {
        onPendingReviewChange?.(pendingReview);
    }, [pendingReview, onPendingReviewChange]);

    return (
        <PositionWalkthroughLayout
            flow={flow}
            viewOnly={viewOnly}
            onSubmitted={onSubmitted}
        />
    );
}

interface PositionWalkthroughLayoutProps extends PositionWalkthroughProps {
    /** The wizard state, as `useStepFlow` returns it. */
    flow: StepFlow;
    /** Pins the rewards-destination treatment (defaults to the flag-driven
     *  one). Only the design-QA showcase sets it. */
    rewardsVariant?: RewardsDestinationVariant;
}

/**
 * The two-column layout, stateless: the step rail (with the Back/Next nav
 * anchored to its bottom) beside the step frame. Split from `PositionWalkthrough` so the design-QA
 * showcase (`/showcase/add-position`) can render every state of the real
 * layout from a fixed `StepFlow`.
 */
export function PositionWalkthroughLayout({
    flow,
    viewOnly,
    onSubmitted,
    rewardsVariant = REWARDS_DESTINATION_VARIANT
}: PositionWalkthroughLayoutProps): React.ReactElement {
    if (flow.submit.submitted && flow.submit.pendingReview) {
        return (
            <ManualVerificationPending
                {...flow.submit.pendingReview}
                onContinue={onSubmitted}
            />
        );
    }
    return (
        <div className="flex flex-col gap-10 xl:flex-row xl:items-stretch xl:gap-12">
            {/* Left column — step accordion (progress overview) with the
                Back/Next nav pinned to its bottom (`mt-auto`). The row stretches
                the column to the step frame's locked height, so the buttons'
                bottom edge sits on the frame's bottom edge. Below xl the column
                is `contents`, so its children join the single-column stack and
                the nav drops to the end (`order-last`): rail, frame, nav. */}
            <div className="contents xl:flex xl:w-96 xl:shrink-0 xl:flex-col xl:gap-6">
                <StepAccordion
                    steps={WIZARD_STEPS}
                    active={flow.nav.active}
                    onSelectStep={flow.nav.setActive}
                    hidden={(index) => isStepHidden(index, rewardsVariant)}
                />
                {/* Next advances the flow; disabled on the last step, where the
                    in-frame "Submit Position" button takes over. */}
                <div className="order-last xl:order-none xl:mt-auto">
                    <WizardNav
                        backDisabled={flow.nav.active === 0}
                        nextDisabled={
                            viewOnly || !flow.nav.canAdvance || flow.nav.isLast
                        }
                        onBack={flow.nav.onBack}
                        onNext={flow.nav.onNext}
                        viewOnly={viewOnly}
                    />
                </div>
            </div>

            {/* Right column — the bordered step frame. */}
            <StepFlowPanel
                source={flow.source}
                find={flow.find}
                verify={flow.verify}
                rewards={flow.rewards}
                submit={flow.submit}
                nav={flow.nav}
                onSubmitted={onSubmitted}
                rewardsVariant={rewardsVariant}
            />
        </div>
    );
}
